import useGetCssValue from '@/hooks/useGetCssValue'
import React, { AllHTMLAttributes, forwardRef } from 'react'

interface IProps extends AllHTMLAttributes<HTMLInputElement> {
  error?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  left?: React.ReactNode
  right?: React.ReactNode
  style?: React.CSSProperties
  height?: string
  className?: string
  name?: string
  id?: string // Ensure input can be associated with a label
}

const InputWithAdornment = forwardRef<HTMLInputElement, IProps>(
  (
    {
      left,
      right,
      onChange,
      placeholder,
      className,
      height = '53px',
      name,
      id,
      error,
      style,
      ...rest
    },
    ref
  ) => {
    const [leftContainer, leftWidth] = useGetCssValue('width', '16px', left)
    const [rightContainer, rightWidth] = useGetCssValue('width', '16px', right)

    const extraWidth =
      (left ? parseFloat(leftWidth) : 0) + (right ? parseFloat(rightWidth) : 0)
    const errorId = error ? `${id}-error` : undefined

    return (
      <div className='flex-1 flex flex-col gap-1 z-10 w-full'>
        <div
          className={`w-full  flex items-center py-0 rounded bg-[#F1F3F4] border border-transparent focus-within:border-jaa-teal-80 
            ${error ? 'border-jaa-red-500 border-solid' : ''}`}
          style={{ ...style, height }}
        >
          {left && <span ref={leftContainer}>{left}</span>}
          <input
            id={id}
            name={name}
            placeholder={placeholder}
            onChange={onChange}
            ref={ref}
            aria-invalid={!!error}
            aria-describedby={errorId}
            style={{
              maxWidth: `calc(100% - ${
                extraWidth + (left || right ? 0 : 16)
              }px)`,
            }}
            {...rest}
            className={`appearance-none max-h-full focus:!shadow-none focus:!ring-none focus:ring-0 focus:outline-none outline-none active:outline-none flex-1 border-none px-4 py-3 text-jaa-shades-black placeholder-jaa-dark-teal-60 bg-[#F1F3F4]  
              ${className || ''} ${left || right ? 'rounded-none' : 'rounded'}`}
          />
          {right && (
            <span className='justify-self-end' ref={rightContainer}>
              {right}
            </span>
          )}
        </div>
        {error && (
          <small
            id={errorId}
            className='text-jaa-red-500 z-10'
            aria-live='polite'
          >
            {error}
          </small>
        )}
      </div>
    )
  }
)

InputWithAdornment.displayName = 'InputWithAdornment'
export default InputWithAdornment
